/**
 * Fuzzy-match a calendar subject (or any freeform title) against a list
 * of project names. Used by the calendar-prefill flow to suggest a
 * project when the user picks an event.
 *
 * Approach: token-set Jaccard with stopwords. Same shape as the
 * server-side duplicate-finalize check so the two pieces of logic
 * behave consistently.
 *
 * OJB project names often have an "XX-XXX - <Name>" prefix. We tokenize
 * past the code and match on the name tokens, so "Duke Park Review" in
 * a calendar matches "23-105 - Duke Park Residences" even without the
 * code.
 */

export interface ProjectMatchCandidate {
  id: string;
  name: string;
  similarity: number;
}

const STOPWORDS = new Set([
  'meeting',
  'meetings',
  'minutes',
  'notes',
  'review',
  'sync',
  'check-in',
  'checkin',
  'update',
  'call',
  'discussion',
  'weekly',
  'biweekly',
  'monthly',
  'the',
  'and',
  'of',
  'for',
  'with',
  'on',
  'at',
  'to',
  'from',
  'ojb',
  'client',
  'internal',
  'team',
  // Typical project-name padding we don't want to drive the match:
  'residences',
  'project',
  'phase',
]);

export function tokenize(s: string): Set<string> {
  return new Set(
    s
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
      .split(/[\s-]+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2 && !STOPWORDS.has(t) && !/^\d+$/.test(t)),
  );
}

export function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const t of a) if (b.has(t)) intersection += 1;
  const union = new Set<string>([...a, ...b]).size;
  return intersection / union;
}

/**
 * Find the best project match for a subject. Returns null when no
 * candidate clears the minimum threshold. Callers should gate how
 * confidently they present the suggestion — a >= 0.5 score is solid,
 * 0.3-0.5 is worth showing as a maybe.
 */
export function suggestProjectForSubject(
  subject: string,
  projects: Array<{ id: string; name: string }>,
  minScore = 0.3,
): ProjectMatchCandidate | null {
  if (!subject.trim() || projects.length === 0) return null;
  const subjectTokens = tokenize(subject);
  if (subjectTokens.size === 0) return null;

  let best: ProjectMatchCandidate | null = null;
  for (const p of projects) {
    const score = jaccard(subjectTokens, tokenize(p.name));
    if (score >= minScore && (!best || score > best.similarity)) {
      best = { id: p.id, name: p.name, similarity: score };
    }
  }
  return best;
}
